import React, { useEffect, useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { getFiltros } from '../services/api';
import { compararFiltrosProfundo } from '../utils/compararFiltros';

function formatTempoAtualizado(date) {
  if (!date) return 'Nunca atualizado';
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin <= 0) return 'Atualizado agora';
  if (diffMin === 1) return 'Atualizado há 1 min';
  if (diffMin < 60) return `Atualizado há ${diffMin} min`;

  const diffHoras = Math.floor(diffMin / 60);
  if (diffHoras === 1) return 'Atualizado há 1 hora';
  return `Atualizado há ${diffHoras} horas`;
}

// Componente de seleção múltipla com busca
const MultiSelect = ({ label, field, selectedValues, onChange, placeholder, optionsList, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        containerRef.current?.contains(event.target) ||
        dropdownRef.current?.contains(event.target)
      ) {
        return;
      }
      setIsOpen(false);
      setSearchTerm('');
    };

    // Usar timeout para não fechar imediatamente ao abrir
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Filtrar opções baseado no termo de busca
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return optionsList;
    const term = searchTerm.toLowerCase();
    return optionsList.filter((opt) => {
      const label = opt.label ?? opt.nome ?? opt.name ?? opt.descricao ?? '';
      return String(label).toLowerCase().includes(term);
    });
  }, [optionsList, searchTerm]);

  // Toggle seleção de um item
  const toggleOption = (value) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    onChange(newValues);
  };

  // Remover um item selecionado
  const removeItem = (value, e) => {
    e.stopPropagation();
    onChange(selectedValues.filter((v) => v !== value));
  };

  // Limpar todos os selecionados
  const clearAll = (e) => {
    e.stopPropagation();
    onChange([]);
    setSearchTerm('');
  };

  // Obter label de um valor
  const getLabel = (value) => {
    const opt = optionsList.find((o) => o.value === value);
    return opt ? (opt.label ?? opt.nome ?? opt.name ?? opt.descricao ?? value) : value;
  };

  // Calcular posição do dropdown com responsividade
  const getDropdownStyle = () => {
    if (!isOpen || !containerRef.current) return {};

    const rect = containerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const isMobile = viewportWidth < 640;
    const isTablet = viewportWidth >= 640 && viewportWidth < 1024;

    let left = rect.left;
    let width = Math.max(rect.width, isMobile ? 280 : 320);

    // Ajustar posição em telas pequenas para não sair da tela
    if (isMobile) {
      const maxWidth = viewportWidth - 16; // 8px de margem de cada lado
      width = Math.min(width, maxWidth);
      left = Math.max(8, Math.min(left, viewportWidth - width - 8));
    } else if (isTablet) {
      width = Math.min(width, viewportWidth * 0.6);
      if (left + width > viewportWidth - 16) {
        left = viewportWidth - width - 16;
      }
    }

    return {
      position: 'fixed',
      top: `${rect.bottom + 4}px`,
      left: `${left}px`,
      width: `${width}px`,
      maxWidth: isMobile ? 'calc(100vw - 16px)' : 'none',
      zIndex: 9999,
    };
  };

  return (
    <>
      <div className="flex flex-col flex-1 relative z-10 min-w-0" ref={containerRef}>
        <span className="text-xs text-gray-700 mb-1.5 whitespace-nowrap font-medium">{label}</span>
        <div className="relative">
          {/* Campo principal */}
          <div
            className={`relative flex items-center min-h-[42px] px-2 sm:px-3 py-2 pr-8 sm:pr-10 text-xs sm:text-sm bg-white border rounded-3xl cursor-pointer ${disabled ? 'border-gray-300 bg-gray-100' : 'border-[#f09005]'
              }`}
            onClick={() => {
              if (!disabled) {
                setIsOpen(true);
                setTimeout(() => inputRef.current?.focus(), 0);
              }
            }}
          >
            {/* Mostrar apenas placeholder ou contagem quando fechado */}
            {selectedValues.length > 0 ? (
              <span className="text-gray-700 text-xs sm:text-sm truncate flex-1">
                {selectedValues.length === 1
                  ? getLabel(selectedValues[0])
                  : `${selectedValues.length} selecionados`}
              </span>
            ) : (
              <span className="text-gray-400 text-xs sm:text-sm truncate flex-1">{placeholder}</span>
            )}

            {/* Botões de ação */}
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {selectedValues.length > 0 && !disabled && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="p-0.5 sm:p-1 text-gray-400 hover:text-gray-600"
                  title="Limpar seleção"
                >
                  <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              <button
                type="button"
                className={`p-0.5 sm:p-1 ${disabled ? 'text-gray-300' : 'text-[#f09005] hover:text-orange-600'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!disabled) {
                    setIsOpen(!isOpen);
                    if (!isOpen) {
                      setTimeout(() => inputRef.current?.focus(), 0);
                    }
                  }
                }}
              >
                <svg className={`w-3 h-3 sm:w-4 sm:h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Dropdown renderizado via Portal */}
      {isOpen && !disabled && createPortal(
        <div
          ref={dropdownRef}
          className="bg-white border border-gray-300 rounded-lg shadow-2xl max-h-[60vh] sm:max-h-60 overflow-hidden"
          style={getDropdownStyle()}
        >
          {/* Campo de busca */}
          <div className="p-1.5 sm:p-2 border-b border-gray-200">
            <input
              ref={inputRef}
              type="text"
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-md outline-none focus:border-[#f09005]"
              placeholder="Digite para buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Lista de opções */}
          <div className="max-h-[50vh] sm:max-h-44 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => {
                const value = opt.value ?? opt.id;
                const optLabel = opt.label ?? opt.nome ?? opt.name ?? opt.descricao;
                const isSelected = selectedValues.includes(value);

                return (
                  <div
                    key={value}
                    className={`flex items-center gap-2 px-2 sm:px-3 py-1.5 sm:py-2 cursor-pointer hover:bg-orange-50 ${isSelected ? 'bg-orange-50' : ''
                      }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleOption(value);
                    }}
                  >
                    <div
                      className={`w-3.5 h-3.5 sm:w-4 sm:h-4 border rounded flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-[#f09005] border-[#f09005]' : 'border-gray-300'
                        }`}
                    >
                      {isSelected && (
                        <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className="text-xs sm:text-sm text-gray-700 truncate">{optLabel}</span>
                  </div>
                );
              })
            ) : (
              <div className="px-2 sm:px-3 py-3 sm:py-4 text-xs sm:text-sm text-gray-500 text-center">
                {searchTerm ? 'Nenhum resultado encontrado' : 'Nenhuma opção disponível'}
              </div>
            )}
          </div>

          {/* Rodapé com contagem */}
          {selectedValues.length > 0 && (
            <div className="px-2 sm:px-3 py-1.5 sm:py-2 border-t border-gray-200 bg-gray-50 text-xs text-gray-500">
              {selectedValues.length} {selectedValues.length === 1 ? 'item selecionado' : 'itens selecionados'}
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

const Filtro = ({ onChange }) => {
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [options, setOptions] = useState({
    postos: [],
    produtos: [],
    estados: [],
    cidades: [],
    funcionarios: [],
    anos: [],
  });

  const [values, setValues] = useState({
    periodo: 'este_mes',
    dataMin: '',
    dataMax: '',
    posto: [],      // Agora é array
    produto: [],    // Agora é array
    estado: [],     // Agora é array
    cidade: [],     // Agora é array
    funcionario: [], // Agora é array
    ano: '',
  });

  const labelTempoAtualizado = useMemo(
    () => formatTempoAtualizado(lastUpdated),
    [lastUpdated]
  );

  const carregarFiltros = async (filtrosAplicados = {}) => {
    try {
      setLoading(true);
      const resposta = await getFiltros(filtrosAplicados);

      const mapearOpcoes = (lista, campo) => {
        return (lista || []).map((item) => {
          const rawValue = item[campo] ?? item[`visao_geral.${campo}`] ?? Object.values(item)[0];
          return {
            value: rawValue,
            label: String(rawValue),
          };
        });
      };

      setOptions((prev) => ({
        postos: mapearOpcoes(resposta.filtro_posto, 'posto'),
        produtos: mapearOpcoes(resposta.filtro_produto, 'nome_produto'),
        estados: mapearOpcoes(resposta.filtro_estado, 'estado'),
        cidades: resposta.filtro_cidade ? mapearOpcoes(resposta.filtro_cidade, 'cidade') : prev.cidades,
        funcionarios: resposta.filtro_funcionario ? mapearOpcoes(resposta.filtro_funcionario, 'funcionario') : prev.funcionarios,
        anos: prev.anos,
      }));

      setLastUpdated(new Date());
    } catch (error) {
      console.error('Erro ao carregar filtros:', error);
    } finally {
      setLoading(false);
    }
  };

  const carregarCidades = async (estados) => {
    if (!estados || estados.length === 0) {
      setOptions((prev) => ({ ...prev, cidades: [] }));
      return;
    }

    try {
      // Usar o primeiro estado selecionado para carregar cidades
      // (ou combinar resultados de múltiplos estados)
      const filtros = { 'visao_geral.estado': estados.join('|||') };
      const resposta = await getFiltros(filtros);

      const cidades = (resposta.filtro_cidade || []).map((item) => {
        const rawValue = item['visao_geral.cidade'] ?? item.cidade ?? Object.values(item)[0];
        return {
          value: rawValue,
          label: String(rawValue),
        };
      });

      setOptions((prev) => ({ ...prev, cidades }));
    } catch (error) {
      console.error('Erro ao carregar cidades:', error);
      setOptions((prev) => ({ ...prev, cidades: [] }));
    }
  };

  const carregarPostos = async (estados, cidades) => {
    try {
      const filtros = {};
      if (estados && estados.length > 0) filtros['visao_geral.estado'] = estados.join('|||');
      if (cidades && cidades.length > 0) filtros['visao_geral.cidade'] = cidades.join('|||');

      const resposta = await getFiltros(filtros);

      const postos = (resposta.filtro_posto || []).map((item) => {
        const rawValue = item['visao_geral.posto'] ?? item.posto ?? Object.values(item)[0];
        return {
          value: rawValue,
          label: String(rawValue),
        };
      });

      setOptions((prev) => ({ ...prev, postos }));
    } catch (error) {
      console.error('Erro ao carregar postos:', error);
    }
  };

  const carregarFuncionarios = async (postos) => {
    if (!postos || postos.length === 0) {
      setOptions((prev) => ({ ...prev, funcionarios: [] }));
      return;
    }

    try {
      const filtros = { 'visao_geral.posto': postos.join('|||') };
      const resposta = await getFiltros(filtros);

      const funcionarios = (resposta.filtro_funcionario || []).map((item) => {
        const rawValue = item['visao_geral.funcionario'] ?? item.funcionario ?? Object.values(item)[0];
        return {
          value: rawValue,
          label: String(rawValue),
        };
      });

      setOptions((prev) => ({ ...prev, funcionarios }));
    } catch (error) {
      console.error('Erro ao carregar funcionários:', error);
      setOptions((prev) => ({ ...prev, funcionarios: [] }));
    }
  };

  // Carregar filtros iniciais
  useEffect(() => {
    carregarFiltros();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recarregar cidades quando estado mudar
  useEffect(() => {
    carregarCidades(values.estado);
  }, [values.estado]);

  // Recarregar postos quando estado ou cidade mudar
  useEffect(() => {
    carregarPostos(values.estado, values.cidade);
  }, [values.estado, values.cidade]);

  // Recarregar funcionários quando posto mudar
  useEffect(() => {
    carregarFuncionarios(values.posto);
  }, [values.posto]);

  // Ref para debounce das atualizações de filtros
  const debounceTimeoutRef = useRef(null);
  const valoresAnterioresRef = useRef(null);

  useEffect(() => {
    // Comparar valores para evitar atualizações desnecessárias
    const valoresIguais = valoresAnterioresRef.current &&
      compararFiltrosProfundo(values, valoresAnterioresRef.current);

    if (valoresIguais) {
      return;
    }

    // Fazer cópia profunda dos valores
    valoresAnterioresRef.current = JSON.parse(JSON.stringify(values));

    // Limpar timeout anterior
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Debounce de 300ms para evitar múltiplas atualizações rápidas
    debounceTimeoutRef.current = setTimeout(() => {
      if (onChange) {
        onChange(values);
      }
    }, 300);

    // Cleanup
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [values, onChange]);

  // Handler para select simples (período)
  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  // Handler para multi-select
  const handleMultiChange = (field) => (newValues) => {
    setValues((prev) => {
      const updated = { ...prev, [field]: newValues };

      // Limpar filtros dependentes quando o filtro pai muda
      if (field === 'estado') {
        updated.cidade = [];
        updated.posto = [];
        updated.funcionario = [];
      } else if (field === 'cidade') {
        updated.posto = [];
        updated.funcionario = [];
      } else if (field === 'posto') {
        updated.funcionario = [];
      }

      return updated;
    });
  };

  // Componente de seleção de período com abas para período customizado
  const PeriodoPicker = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(values.periodo === 'customizado' ? 'personalizado' : 'predefinicoes');
    const containerRef = useRef(null);
    const dropdownRef = useRef(null);
    const [customDataMinDraft, setCustomDataMinDraft] = useState(values.dataMin || '');
    const [customDataMaxDraft, setCustomDataMaxDraft] = useState(values.dataMax || '');
    const [dataError, setDataError] = useState('');

    // Sincronizar aba ativa com o período selecionado
    useEffect(() => {
      if (values.periodo === 'customizado') {
        setActiveTab('personalizado');
      } else {
        setActiveTab('predefinicoes');
      }
    }, [values.periodo]);

    // Sincronizar rascunho do personalizado quando abrir o dropdown
    useEffect(() => {
      if (!isOpen) {
        setDataError(''); // Limpar erro ao fechar
        return;
      }
      setCustomDataMinDraft(values.dataMin || '');
      setCustomDataMaxDraft(values.dataMax || '');
      setDataError(''); // Limpar erro ao abrir
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Fechar dropdown ao clicar fora
    useEffect(() => {
      if (!isOpen) return;

      const handleClickOutside = (event) => {
        if (
          containerRef.current?.contains(event.target) ||
          dropdownRef.current?.contains(event.target)
        ) {
          return;
        }
        setIsOpen(false);
      };

      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen]);

    const formatarDataRange = (dataMin, dataMax) => {
      if (!dataMin || !dataMax) return '';
      // Converter de YYYY-MM-DD para YYYY/MM/DD
      return `${dataMin.replace(/-/g, '/')} - ${dataMax.replace(/-/g, '/')}`;
    };

    const getDisplayValue = () => {
      if (values.periodo === 'customizado') {
        return formatarDataRange(values.dataMin, values.dataMax) || 'Selecione o período';
      }
      const selectedOption = periodoOptions.find(opt => opt.value === values.periodo);
      return selectedOption ? selectedOption.label : 'Selecione um período';
    };

    const handleTabChange = (tab) => {
      setActiveTab(tab);
      if (tab === 'personalizado') {
        // Ao abrir a aba personalizado, manter rascunho sincronizado com o valor atual
        setCustomDataMinDraft(values.dataMin || '');
        setCustomDataMaxDraft(values.dataMax || '');
      }
    };

    const handleCustomDateDraftChange = (field) => (e) => {
      const value = e.target.value;
      if (field === 'dataMin') {
        setCustomDataMinDraft(value);
        // Validar se data inicial é maior que data final
        if (value && customDataMaxDraft && value > customDataMaxDraft) {
          setDataError('A data inicial não pode ser maior que a data final.');
        } else {
          setDataError('');
        }
      }
      if (field === 'dataMax') {
        setCustomDataMaxDraft(value);
        // Validar se data inicial é maior que data final
        if (customDataMinDraft && value && customDataMinDraft > value) {
          setDataError('A data inicial não pode ser maior que a data final.');
        } else {
          setDataError('');
        }
      }
    };

    const applyCustomPeriod = () => {
      if (!customDataMinDraft || !customDataMaxDraft) return;

      // Validar se há erro antes de aplicar
      if (customDataMinDraft > customDataMaxDraft) {
        setDataError('A data inicial não pode ser maior que a data final.');
        return;
      }

      setDataError('');

      setValues((prev) => ({
        ...prev,
        periodo: 'customizado',
        dataMin: customDataMinDraft,
        dataMax: customDataMaxDraft,
      }));

      // Após aplicar, fechar o dropdown
      setIsOpen(false);
    };

    const getDropdownStyle = () => {
      if (!containerRef.current) return {};
      const rect = containerRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth < 640;
      const isTablet = window.innerWidth >= 640 && window.innerWidth < 1024;

      return {
        position: 'fixed',
        top: `${rect.bottom + 4}px`,
        left: `${rect.left}px`,
        width: isMobile ? 'calc(100vw - 16px)' : isTablet ? '60vw' : `${Math.max(rect.width, 300)}px`,
        maxWidth: isMobile ? 'calc(100vw - 16px)' : isTablet ? '60vw' : 'none',
        zIndex: 9999,
      };
    };

    return (
      <>
        <div className="flex flex-col flex-wrap flex-1 relative z-10 min-w-0" ref={containerRef}>
          <span className="text-xs text-gray-700 mb-1.5 whitespace-nowrap font-medium">Por período</span>
          <div className="relative flex items-center rounded-3xl border border-[#f09005] w-full flex-shrink-0 bg-white">
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="flex-1 px-2 sm:px-3 py-2 sm:py-2.5 pr-8 sm:pr-10 text-xs sm:text-sm text-gray-700 bg-white border-0 outline-none rounded-3xl cursor-pointer text-left truncate"
            >
              {getDisplayValue()}
            </button>
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="absolute right-0 flex items-center justify-center w-7 sm:w-9 h-full bg-transparent text-[#f09005] hover:text-orange-600 transition-colors cursor-pointer pointer-events-auto"
              aria-label="Abrir período"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Dropdown com abas */}
        {isOpen && createPortal(
          <div
            ref={dropdownRef}
            className="bg-white border border-[#f09005] rounded-3xl shadow-lg overflow-hidden"
            style={getDropdownStyle()}
          >
            {/* Abas */}
            <div className="flex">
              <button
                type="button"
                onClick={() => handleTabChange('predefinicoes')}
                className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors relative ${activeTab === 'predefinicoes'
                  ? 'text-[#f09005] bg-orange-50'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                  }`}
              >
                Predefinições
                {activeTab === 'predefinicoes' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#f09005]" />
                )}
              </button>
              <button
                type="button"
                onClick={() => handleTabChange('personalizado')}
                className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors relative ${activeTab === 'personalizado'
                  ? 'text-[#f09005] bg-orange-50'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                  }`}
              >
                Personalizado
                {activeTab === 'personalizado' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#f09005]" />
                )}
              </button>
            </div>

            {/* Linha divisória */}
            <div className="border-b border-gray-200" />

            {/* Conteúdo das abas */}
            <div className="p-3">
              {activeTab === 'predefinicoes' ? (
                <select
                  className="w-full px-3 py-2.5 text-sm text-gray-700 bg-white border border-[#f09005] rounded-3xl outline-none cursor-pointer"
                  value={values.periodo === 'customizado' ? '' : values.periodo}
                  onChange={(e) => {
                    handleChange('periodo')(e);
                  }}
                >
                  <option value="" disabled>
                    Selecione um período
                  </option>
                  {periodoOptions.filter(opt => opt.value !== 'customizado').map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-700 mb-1.5 font-medium">Data inicial</label>
                    <input
                      type="date"
                      className={`w-full px-3 py-2.5 text-sm text-gray-700 bg-white border rounded-3xl outline-none cursor-pointer ${dataError ? 'border-red-500' : 'border-[#f09005]'}`}
                      value={customDataMinDraft}
                      onChange={handleCustomDateDraftChange('dataMin')}
                      max={'2099-12-31'}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-700 mb-1.5 font-medium">Data final</label>
                    <input
                      type="date"
                      className={`w-full px-3 py-2.5 text-sm text-gray-700 bg-white border rounded-3xl outline-none cursor-pointer ${dataError ? 'border-red-500' : 'border-[#f09005]'}`}
                      value={customDataMaxDraft}
                      onChange={handleCustomDateDraftChange('dataMax')}
                      min={'2000-01-01'}
                      max="2099-12-31"
                    />
                  </div>

                  {dataError && (
                    <div className="px-3 py-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg">
                      {dataError}
                    </div>
                  )}

                  <div className="flex items-center justify-end gap-2 pt-1">
                    <button
                      type="button"
                      onClick={applyCustomPeriod}
                      disabled={!customDataMinDraft || !customDataMaxDraft || !!dataError}
                      className="px-4 py-2 text-xs sm:text-sm font-medium rounded-3xl bg-[#f09005] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-orange-600 transition-colors"
                      title="Aplicar período personalizado"
                    >
                      Aplicar
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
      </>
    );
  };

  const renderSelect = (label, field, value, onChange, placeholder, optionsList) => {
    const selectId = `select-${field}`;

    const handleButtonClick = (e) => {
      e.preventDefault();
      const selectElement = document.getElementById(selectId);
      if (selectElement) {
        selectElement.focus();
        selectElement.click();
      }
    };

    const selectedOption = optionsList.find(opt => (opt.value ?? opt.id) === value);
    const displayValue = selectedOption ? (selectedOption.label ?? selectedOption.nome ?? selectedOption.name ?? selectedOption.descricao) : '';

    return (
      <div className="flex flex-col flex-1 relative z-10 min-w-0">
        <span className="text-xs text-gray-700 mb-1.5 whitespace-nowrap font-medium">{label}</span>
        <div className="relative flex items-center rounded-3xl border border-[#f09005] w-full flex-shrink-0 bg-white">
          <select
            id={selectId}
            className="flex-1 px-2 sm:px-3 py-2 sm:py-2.5 pr-8 sm:pr-10 text-xs sm:text-sm text-gray-700 bg-white border-0 outline-none appearance-none rounded-3xl cursor-pointer"
            value={value}
            onChange={onChange}
          >
            <option value="">{placeholder}</option>
            {optionsList.map((opt) => (
              <option key={opt.value ?? opt.id} value={opt.value ?? opt.id}>
                {opt.label ?? opt.nome ?? opt.name ?? opt.descricao}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleButtonClick}
            className="absolute right-0 flex items-center justify-center w-7 sm:w-9 h-full bg-transparent text-[#f09005] hover:text-orange-600 transition-colors cursor-pointer pointer-events-auto"
            aria-label={`Abrir ${label}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  const periodoOptions = [
    { value: 'hoje', label: 'Hoje' },
    { value: 'ontem', label: 'Ontem' },
    { value: 'esta_semana', label: 'Esta semana' },
    { value: 'semana_passada', label: 'Semana passada' },
    { value: 'este_mes', label: 'Este mês' },
    { value: 'mes_passado', label: 'Mês passado' },
    { value: 'este_ano', label: 'Este ano' },
    { value: 'ano_passado', label: 'Ano passado' },
    { value: 'customizado', label: 'Período customizado' },
  ];

  // Atualizar datas quando período mudar (exceto customizado)
  useEffect(() => {
    if (values.periodo && values.periodo !== 'customizado') {
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);

      let dataMin = null;
      let dataMax = null;

      switch (values.periodo) {
        case 'hoje':
          dataMin = new Date(hoje);
          dataMax = new Date(hoje);
          break;
        case 'ontem':
          const ontem = new Date(hoje);
          ontem.setDate(ontem.getDate() - 1);
          dataMin = ontem;
          dataMax = ontem;
          break;
        case 'esta_semana':
          const inicioSemana = new Date(hoje);
          inicioSemana.setDate(hoje.getDate() - hoje.getDay());
          dataMin = inicioSemana;
          dataMax = new Date(hoje);
          break;
        case 'semana_passada':
          const inicioSemanaPassada = new Date(hoje);
          inicioSemanaPassada.setDate(hoje.getDate() - hoje.getDay() - 7);
          const fimSemanaPassada = new Date(inicioSemanaPassada);
          fimSemanaPassada.setDate(inicioSemanaPassada.getDate() + 6);
          dataMin = inicioSemanaPassada;
          dataMax = fimSemanaPassada;
          break;
        case 'este_mes':
          const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
          dataMin = inicioMes;
          dataMax = new Date(hoje);
          break;
        case 'mes_passado':
          const inicioMesPassado = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
          const fimMesPassado = new Date(hoje.getFullYear(), hoje.getMonth(), 0);
          dataMin = inicioMesPassado;
          dataMax = fimMesPassado;
          break;
        case 'este_ano':
          const inicioAno = new Date(hoje.getFullYear(), 0, 1);
          dataMin = inicioAno;
          dataMax = new Date(hoje);
          break;
        case 'ano_passado':
          const inicioAnoPassado = new Date(hoje.getFullYear() - 1, 0, 1);
          const fimAnoPassado = new Date(hoje.getFullYear() - 1, 11, 31);
          dataMin = inicioAnoPassado;
          dataMax = fimAnoPassado;
          break;
        default:
          return;
      }

      // Formatar datas como YYYY-MM-DD
      const formatarData = (data) => {
        if (!data) return '';
        const ano = data.getFullYear();
        const mes = String(data.getMonth() + 1).padStart(2, '0');
        const dia = String(data.getDate()).padStart(2, '0');
        return `${ano}-${mes}-${dia}`;
      };

      setValues((prev) => ({
        ...prev,
        dataMin: formatarData(dataMin),
        dataMax: formatarData(dataMax),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.periodo]);

  return (
    <div className="w-full flex flex-col bg-transparent relative z-10">
      {/* Filtros responsivos - grid em mobile, flex em desktop */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-3 pb-2">
        <PeriodoPicker />

        <MultiSelect
          label="Posto"
          field="posto"
          selectedValues={values.posto}
          onChange={handleMultiChange('posto')}
          placeholder="Selecione o posto"
          optionsList={options.postos}
        />

        <MultiSelect
          label="Produto"
          field="produto"
          selectedValues={values.produto}
          onChange={handleMultiChange('produto')}
          placeholder="Selecione o produto"
          optionsList={options.produtos}
        />

        <MultiSelect
          label="Região"
          field="estado"
          selectedValues={values.estado}
          onChange={handleMultiChange('estado')}
          placeholder="Selecione a região"
          optionsList={options.estados}
        />

        <MultiSelect
          label="Cidade"
          field="cidade"
          selectedValues={values.cidade}
          onChange={handleMultiChange('cidade')}
          placeholder="Selecione a cidade"
          optionsList={options.cidades}
        />

        <MultiSelect
          label="Funcionário"
          field="funcionario"
          selectedValues={values.funcionario}
          onChange={handleMultiChange('funcionario')}
          placeholder="Selecione o funcionário"
          optionsList={options.funcionarios}
        />
      </div>
    </div>
  );
};

export default Filtro;
